"use client";

// Step 6.7.3.1 / AC188 + AC189: client wrapper that adds chevron
// navigation + mouse click-and-drag to the MoreFromCategory carousel.
// Native touch swipe still works via overflow-x: auto (no extra handlers
// needed there). The wrapper takes pre-rendered card slides as children
// so the server component upstream can still SSR them.

import { useCallback, useEffect, useRef, useState } from "react";

const DRAG_THRESHOLD_PX = 5;

export function CarouselScroller({ children }: { children: React.ReactNode }) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  // Mouse-drag bookkeeping. Refs because we don't want a re-render on
  // every mousemove.
  const dragStateRef = useRef<{
    isDown: boolean;
    startX: number;
    startScrollLeft: number;
    moved: number;
  }>({ isDown: false, startX: 0, startScrollLeft: 0, moved: 0 });

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < maxScroll - 4);
  }, []);

  useEffect(() => {
    updateScrollState();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);

    // Native mousedown listener (bypasses React synthetic events so
    // preventDefault is honored before the browser's native link-drag
    // handler kicks in for <Link> children).
    function nativeMouseDown(e: MouseEvent) {
      if (e.button !== 0) return; // left-click only
      e.preventDefault();
      dragStateRef.current = {
        isDown: true,
        startX: e.clientX,
        startScrollLeft: el!.scrollLeft,
        moved: 0,
      };
      el!.style.cursor = "grabbing";
      el!.style.userSelect = "none";
      el!.style.scrollSnapType = "none";
      document.addEventListener("mousemove", onDocMouseMove);
      document.addEventListener("mouseup", onDocMouseUp);
    }
    function onDocMouseMove(e: MouseEvent) {
      const state = dragStateRef.current;
      if (!state.isDown) return;
      const dx = e.clientX - state.startX;
      state.moved = Math.max(state.moved, Math.abs(dx));
      el!.scrollLeft = state.startScrollLeft - dx;
    }
    function onDocMouseUp() {
      dragStateRef.current.isDown = false;
      el!.style.cursor = "grab";
      el!.style.userSelect = "";
      el!.style.scrollSnapType = "";
      document.removeEventListener("mousemove", onDocMouseMove);
      document.removeEventListener("mouseup", onDocMouseUp);
    }
    el.addEventListener("mousedown", nativeMouseDown);

    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
      el.removeEventListener("mousedown", nativeMouseDown);
      document.removeEventListener("mousemove", onDocMouseMove);
      document.removeEventListener("mouseup", onDocMouseUp);
    };
  }, [updateScrollState]);

  function cardWidth(): number {
    const el = scrollRef.current;
    if (!el) return 0;
    const first = el.querySelector(
      "[data-carousel-card]",
    ) as HTMLElement | null;
    if (!first) return 0;
    // Card outer width + gap (the parent flex's gap-4 = 16px).
    return first.offsetWidth + 16;
  }

  function scrollByCards(delta: number) {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: cardWidth() * delta, behavior: "smooth" });
  }

  // Suppress card-Link clicks when the user just finished a drag - the
  // mouseup fires before the click event so we can read dragStateRef.
  function onClickCapture(e: React.MouseEvent) {
    if (dragStateRef.current.moved > DRAG_THRESHOLD_PX) {
      e.preventDefault();
      e.stopPropagation();
      // Reset so a subsequent click without drag still works.
      dragStateRef.current.moved = 0;
    }
  }

  return (
    <div className="relative">
      <button
        type="button"
        data-testid="carousel-chevron-left"
        aria-label="Scroll left"
        onClick={() => scrollByCards(-1)}
        className={
          (canScrollLeft ? "" : "pointer-events-none opacity-0 ") +
          "absolute left-0 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-brand-border bg-white text-brand-text shadow transition-opacity"
        }
      >
        <ChevronIcon dir="left" />
      </button>
      <button
        type="button"
        data-testid="carousel-chevron-right"
        aria-label="Scroll right"
        onClick={() => scrollByCards(1)}
        className={
          (canScrollRight ? "" : "pointer-events-none opacity-0 ") +
          "absolute right-0 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-brand-border bg-white text-brand-text shadow transition-opacity"
        }
      >
        <ChevronIcon dir="right" />
      </button>
      <div
        ref={scrollRef}
        data-testid="carousel-scroll"
        onClickCapture={onClickCapture}
        className="flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth pb-4"
        style={{ cursor: "grab", scrollbarWidth: "thin" }}
      >
        {children}
      </div>
    </div>
  );
}

function ChevronIcon({ dir }: { dir: "left" | "right" }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {dir === "left" ? (
        <polyline points="15 18 9 12 15 6" />
      ) : (
        <polyline points="9 18 15 12 9 6" />
      )}
    </svg>
  );
}
